import React from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Tiny indicator next to a sortable column header.
 * Pairs with the SortableHeader button below.
 */
export function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  return dir === "asc"
    ? <ArrowUp className="w-3 h-3 text-primary" />
    : <ArrowDown className="w-3 h-3 text-primary" />;
}

/**
 * Drop-in clickable column header that toggles sort direction.
 *
 * Props:
 *  - sortKey (string) — current active sort column key
 *  - sortDir ("asc" | "desc") — current direction
 *  - columnKey (string) — this column's key
 *  - onToggle (fn, columnKey) — called on click
 *  - className — extra classes (e.g. grid-span)
 *  - testId — data-testid for the button
 */
export function SortableHeader({ sortKey, sortDir, columnKey, onToggle, className = "", testId, children }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(columnKey)}
      className={`flex items-center gap-1 hover:text-primary ${className}`}
      data-testid={testId}
    >
      {children} <SortIcon active={sortKey === columnKey} dir={sortDir} />
    </button>
  );
}

/**
 * Hook returning [sortKey, sortDir, toggleSort].
 * Default ascending on first click of a new column; toggles otherwise.
 */
export function useSort(defaultKey, defaultDir = "asc") {
  const [{ sortKey, sortDir }, setSort] = React.useState({ sortKey: defaultKey, sortDir: defaultDir });
  const toggleSort = React.useCallback((key) => {
    setSort(previous => ({ sortKey: key, sortDir: previous.sortKey === key && previous.sortDir === "asc" ? "desc" : "asc" }));
  }, []);
  return { sortKey, sortDir, toggleSort };
}

/**
 * Generic comparator factory.
 * `getValue(item)` should return string|number for the active column.
 */
export function makeComparator(getValue, dir) {
  const sign = dir === "asc" ? 1 : -1;
  return (a, b) => {
    const va = getValue(a);
    const vb = getValue(b);
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
    return String(va || "").localeCompare(String(vb || "")) * sign;
  };
}
